import { type ReactNode } from 'react';

import { API_BASE_URL } from '@/api/client';
import { apiOriginProblem, type ApiOriginProblem } from '@/api/origin';
import { Alert } from '@/components/ui/Alert';

import styles from './ConfigurationGuard.module.css';

/*
 * One sentence per problem, in the reader's language, saying what will not work
 * rather than which variable is unset. The variable belongs in the paragraph
 * below, addressed to whoever deployed the site — a visitor cannot act on it,
 * but the person they will complain to can, and this screen is the only place
 * that information exists at all.
 */
const REASONS: Record<ApiOriginProblem, string> = {
    missing: 'Deze versie van de site weet niet waar de server staat.',
    unparseable: 'Het serveradres in deze versie van de site is geen geldig webadres.',
    loopback:
        'Deze versie van de site zoekt de server op jouw eigen computer in plaats van op ' +
        'internet.',
    insecure:
        'Deze pagina is beveiligd met https, maar het serveradres niet — je browser ' +
        'blokkeert dan elk verzoek.',
};

interface Props {
    children: ReactNode;
}

/**
 * Refuses to start the application when the API origin baked into the bundle
 * cannot work on the host serving it.
 *
 * This exists because the alternative is worse than a blank page: the shell,
 * the navigation and every empty state render exactly as designed, so the site
 * looks finished and blames the reader's data for the fact that nothing loads.
 * "Je hebt nog geen trainingen" is what a build pointed at the wrong origin
 * says on every screen at once.
 *
 * It sits outside `AppProviders` on purpose — there is no point creating a query
 * client for requests that cannot be addressed.
 */
export function ConfigurationGuard({ children }: Props) {
    const problem = apiOriginProblem(API_BASE_URL, window.location);

    if (problem === null) {
        return <>{children}</>;
    }

    /*
     * Also in the console, where a developer looks first. Logged during render
     * rather than in an effect because the render is the last thing that
     * happens here; StrictMode doubles it in development and not in the
     * production build, which is the one that matters.
     */
    console.error(
        `[ThuisCoach] VITE_API_BASE_URL is ${API_BASE_URL === '' ? 'niet gezet' : `"${API_BASE_URL}"`}, ` +
            `wat niet werkt op ${window.location.origin} (${problem}).`,
    );

    return (
        <div className={styles.screen} role="alert">
            <div className={styles.panel}>
                <h1 className={styles.title}>ThuisCoach is niet ingesteld</h1>

                <Alert tone="danger">{`${REASONS[problem]} Er kan daarom niets worden geladen of opgeslagen.`}</Alert>

                <p className={styles.help}>
                    Beheer je deze site? Zet <code className={styles.code}>VITE_API_BASE_URL</code>{' '}
                    op het adres van de API en bouw de site opnieuw. Het adres wordt bij het bouwen
                    vastgelegd, dus een nieuwe waarde werkt pas na een nieuwe build.
                </p>

                <p className={styles.help}>
                    Nu vastgelegd:{' '}
                    <code className={styles.code}>
                        {API_BASE_URL === '' ? 'geen adres' : API_BASE_URL}
                    </code>
                </p>
            </div>
        </div>
    );
}
